import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Form,
  Modal,
  Alert,
  Table,
  Spinner,
  OverlayTrigger,
  Tooltip as BSTooltip
} from 'react-bootstrap';
import {
  FaFileContract,
  FaUser,
  FaHome,
  FaFileAlt,
  FaEdit,
  FaDownload,
  FaEye,
  FaCheckCircle,
  FaExclamationTriangle,
  FaUsers,
  FaPaperPlane,
  FaCog,
  FaLightbulb,
  FaExternalLinkAlt,
  FaFilePdf
} from 'react-icons/fa';
import axiosInstance from '../../helpers/api';
import SmartDocumentChecklistProduction from '../SmartDocumentChecklistProduction';

interface Template {
  id: string;
  template_name: string;
  document_type: string;
  anvil_template_id: string;
  pdffiller_template_id?: string | null;
  pdffiller_fillable_id?: string | null;
  pdffiller_edit_url?: string | null;
  pdffiller_template_uuid?: string | null;
  is_fillable_template?: number | boolean;
  field_mappings: {
    parties?: string[];
    fields?: Record<string, string>;
  };
  is_used: boolean;
}

interface TransactionParty {
  type: string;
  name: string;
  email: string;
  phone: string;
  address?: string;
}

interface TransactionData {
  property_address: string;
  property_city: string;
  property_state: string;
  property_zip: string;
  purchase_price: string;
  closing_date: string;
  buyer_name: string;
  buyer_email: string;
  buyer_phone: string;
  seller_name: string;
  seller_email: string;
  seller_phone: string;
  buyer_agent_name?: string;
  buyer_agent_email?: string;
  seller_agent_name?: string;
  seller_agent_email?: string;
}

interface TransactionTemplateManagerProps {
  transactionId: string;
  transactionData?: TransactionData;
  brokerageId?: string;
  onDocumentsCopied?: () => void;
}

const partyTypeConfig = {
  buyer: { color: 'success', icon: FaUser, label: 'Buyer' },
  seller: { color: 'primary', icon: FaHome, label: 'Seller' },
  buyer_agent: { color: 'info', icon: FaUser, label: "Buyer's Agent" },
  seller_agent: { color: 'warning', icon: FaUser, label: "Seller's Agent" },
  all: { color: 'secondary', icon: FaUsers, label: 'All Parties' }
};

const categoryConfig = {
  contract: { color: 'success', label: 'Contracts' },
  agreement: { color: 'primary', label: 'Agreements' },
  disclosure: { color: 'warning', label: 'Disclosures' },
  advisory: { color: 'info', label: 'Advisories' },
  addendum: { color: 'secondary', label: 'Addendums' },
  warranty: { color: 'success', label: 'Warranties' }
};

export const TransactionTemplateManager: React.FC<TransactionTemplateManagerProps> = ({
  transactionId,
  transactionData,
  brokerageId,
  onDocumentsCopied
}) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFieldMappingModal, setShowFieldMappingModal] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState<Template | null>(null);
  const [templateFields, setTemplateFields] = useState<any[]>([]);
  const [fieldMappings, setFieldMappings] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchTemplates();
  }, [transactionId]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/transaction-templates/transaction/${transactionId}/templates`);

      const normalizedTemplates: Template[] = (response.data.templates || []).map((template: any) => ({
        ...template,
        pdffiller_template_id: template?.pdffiller_template_id ? String(template.pdffiller_template_id) : null,
        pdffiller_fillable_id: template?.pdffiller_fillable_id ? String(template.pdffiller_fillable_id) : null,
        pdffiller_edit_url: template?.pdffiller_edit_url ?? null,
        pdffiller_template_uuid: template?.pdffiller_template_uuid ?? null,
        is_fillable_template: template?.is_fillable_template ?? false
      }));

      setTemplates(normalizedTemplates);
    } catch (error) {
      console.error('Error fetching templates:', error);
      alert('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };


  const handleConfigureMapping = async (template: Template) => {
    setCurrentTemplate(template);
    setShowFieldMappingModal(true);
    
    try {
      const response = await axiosInstance.get(
        `/transaction-templates/template/${template.anvil_template_id}/fields`
      );
      
      setTemplateFields(response.data.fields);
      setFieldMappings(response.data.suggestions || {});
    } catch (error) {
      console.error('Error fetching template fields:', error);
      alert('Failed to load template fields');
    }
  };

  const handleSaveFieldMappings = async () => {
    if (!currentTemplate) return;
    
    try {
      await axiosInstance.post(
        `/transaction-templates/template/${currentTemplate.anvil_template_id}/field-mappings`,
        {
          parties: Object.values(fieldMappings).filter((v, i, a) => a.indexOf(v) === i),
          fields: fieldMappings
        }
      );
      
      alert('Field mappings saved successfully');
      setShowFieldMappingModal(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error saving field mappings:', error);
      alert('Failed to save field mappings');
    }
  };


  const getTemplateCategory = (type: string) => {
    if (type.includes('agreement')) return 'agreement';
    if (type.includes('disclosure')) return 'disclosure';
    if (type.includes('advisory')) return 'advisory';
    if (type.includes('addendum')) return 'addendum';
    if (type.includes('warranty')) return 'warranty';
    return 'contract';
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      {/* Smart Document Checklist Widget */}
      <div className="mb-4">
        <Alert variant="info" className="d-flex align-items-center mb-3">
          <FaLightbulb className="me-2" style={{ color: '#00d4ff' }} />
          <strong>Pro Tip:</strong> Update the property and transaction details below to see which documents are required for your specific deal.
        </Alert>
        <SmartDocumentChecklistProduction 
          brokerageId={brokerageId || "42"}
          transactionId={transactionId}
          onDocumentSelect={async (documentId) => {
            console.log('🔵 [Smart Checklist] Document selected:', documentId);
            
            try {
              console.log('🟡 [Smart Checklist] Calling inherit-documents API...');
              const response = await axiosInstance.post(`/transactions/${transactionId}/inherit-documents`, {
                documents: [{
                  templateId: documentId,
                  templateName: 'Document from Smart Checklist',
                  reason: 'Selected from Smart Document Checklist'
                }]
              });
              
              console.log('🟣 [Smart Checklist] API Response:', response.data);
              
              if (response.data.success) {
                alert('Document added to transaction successfully!');
                // Notify parent to refresh documents
                if (onDocumentsCopied) {
                  onDocumentsCopied();
                }
              }
            } catch (error) {
              console.error('Error adding document:', error);
              alert('Failed to add document to transaction');
            }
          }}
        />
      </div>

      {/* Existing Templates Section - COMMENTED OUT as it's redundant with Smart Document Checklist above */}
      {/* <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>Transaction Document Templates</h5>
<Badge bg="info">
              {templates.filter(t => !t.is_used).length} Templates Available
            </Badge>
          </div>

          {transactionData && (
            <Alert variant="info" className="mb-3">
              Property: {transactionData.property_address}, {transactionData.property_city}, {transactionData.property_state}
            </Alert>
          )}

          <Row>
            {templates.map((template) => {
              const category = getTemplateCategory(template.document_type);
              const categoryInfo = categoryConfig[category as keyof typeof categoryConfig];
              const hasFieldMappings = template.field_mappings?.fields && 
                Object.keys(template.field_mappings.fields).length > 0;

              return (
                <Col xs={12} sm={6} md={4} key={template.id} className="mb-3">
                  <Card 
                    className={`h-100 ${template.is_used ? 'border-success' : ''}`}
                    style={{ 
                      borderWidth: template.is_used ? 2 : 1,
                      backgroundColor: template.is_used ? '#f0fff4' : 'white'
                    }}
                  >
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 className="mb-0">{template.template_name}</h6>
                        </div>
                        <Badge bg={categoryInfo.color}>
                          {categoryInfo.label}
                        </Badge>
                      </div>


                      <div className="mb-3">
                        {template.is_used && (
                          <Badge bg="success" className="me-2">
                            <FaCheckCircle className="me-1" />
                            Added to Transaction
                          </Badge>
                        )}
                        {!template.is_used && hasFieldMappings && (
                          <Badge bg="info" className="me-2">
                            <FaCheckCircle className="me-1" />
                            Ready to Use
                          </Badge>
                        )}
                        {!template.is_used && !hasFieldMappings && (
                          <Badge bg="secondary" className="me-2">
                            <FaFileAlt className="me-1" />
                            Template Available
                          </Badge>
                        )}
                        {template.pdffiller_template_id && (
                          <Badge bg="primary" className="me-2">
                            <FaFilePdf className="me-1" />
                            PDFFiller Linked
                          </Badge>
                        )}
                      </div>

                      {template.field_mappings?.parties && (
                        <div className="mb-2">
                          <small className="text-muted">Parties:</small>
                          <div className="d-flex flex-wrap gap-1 mt-1">
                            {template.field_mappings.parties.map((party) => {
                              const config = partyTypeConfig[party as keyof typeof partyTypeConfig];
                              return (
                                <Badge
                                  key={party}
                                  bg={config?.color || 'secondary'}
                                >
                                  {config?.label || party}
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div className="d-flex justify-content-end gap-2">
                        {template.pdffiller_edit_url && (
                          <Button
                            size="sm"
                            variant="outline-info"
                            onClick={() => {
                              if (template.pdffiller_edit_url) {
                                window.open(template.pdffiller_edit_url, '_blank', 'noopener');
                              }
                            }}
                            title="Open this template in PDFFiller"
                          >
                            <FaExternalLinkAlt className="me-1" />
                            Open in PDFFiller
                          </Button>
                        )}
                        {template.is_used ? (
                          <Badge bg="success">
                            <FaCheckCircle className="me-1" />
                            Already Added
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={async () => {
                              console.log('🟢 [Templates Section] Use Template clicked:', template);
                              try {
                                console.log('🟡 [Templates Section] Calling inherit-documents API...');
                                const response = await axiosInstance.post(`/transactions/${transactionId}/inherit-documents`, {
                                  documents: [{
                                    templateId: template.id,
                                    templateName: template.template_name,
                                    reason: 'Selected from Templates section'
                                  }]
                                });
                                
                                console.log('🟣 [Templates Section] API Response:', response.data);
                                
                                if (response.data.success) {
                                  const successCount = response.data.results.filter((r: any) => r.success).length;
                                  if (successCount > 0) {
                                    alert(`Template "${template.template_name}" added to transaction successfully!`);
                                    fetchTemplates();
                                    if (onDocumentsCopied) {
                                      onDocumentsCopied();
                                    }
                                  } else {
                                    alert('Failed to add template. It may not be available in your brokerage templates.');
                                  }
                                }
                              } catch (error) {
                                console.error('Error adding template:', error);
                                alert('Failed to add template to transaction');
                              }
                            }}
                            title="Add this template to the transaction"
                          >
                            <FaDownload className="me-1" />
                            Use Template
                          </Button>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card.Body>
      </Card> */}

      {/* Field Mapping Modal */}
      <Modal
        show={showFieldMappingModal}
        onHide={() => setShowFieldMappingModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Configure Field Mappings - {currentTemplate?.template_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-3">
            <FaFileContract className="me-2" />
            Assign each field to the appropriate party. Fields assigned to a party will only appear in their version of the document.
          </Alert>

          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>Field Name</th>
                <th>Assigned To</th>
              </tr>
            </thead>
            <tbody>
              {templateFields.map((field) => (
                <tr key={field.name}>
                  <td>
                    {field.name}
                    {field.required && (
                      <Badge bg="danger" className="ms-2">Required</Badge>
                    )}
                  </td>
                  <td>
                    <Form.Select
                      size="sm"
                      value={fieldMappings[field.name] || ''}
                      onChange={(e) => setFieldMappings({
                        ...fieldMappings,
                        [field.name]: e.target.value
                      })}
                    >
                      <option value="">Unassigned</option>
                      {Object.entries(partyTypeConfig).map(([key, config]) => (
                        <option key={key} value={key}>
                          {config.label}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFieldMappingModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveFieldMappings}
          >
            Save Mappings
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TransactionTemplateManager;
